import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import type { SiteNonDetect } from "../../../../apiClient/generated";
import { useAppSelector, useNondetectsApiClient } from "../../../../hooks";
import { formatDateTime } from "../../../../utils/dates";
import { CompactTableUI, useDataTableV3 } from "../../../DataTable";
import { RateWithUncertaintyCell } from "../../../Emissions/Extras";

const nonDetectsColumnHelper = createColumnHelper<SiteNonDetect>();
const nonDetectsColumns = [
    nonDetectsColumnHelper.accessor("timestamp", {
        id: "timestamp",
        cell: ({ getValue }) => formatDateTime(getValue()),
        header: "Timestamp (UTC)",
        sortingFn: (rowA, rowB) => {
            return (
                rowA.original.timestamp.getTime() -
                rowB.original.timestamp.getTime()
            );
        },
    }),
    nonDetectsColumnHelper.accessor(
        (row) => ({
            providerName: row.dataProvider.name,
            secondaryDataSource: row.secondaryDataSource,
        }),
        {
            id: "provider",
            cell: (info) => {
                const value = info.getValue();
                return `${value.providerName} ${
                    value.secondaryDataSource?.trim()
                        ? "(" + value.secondaryDataSource + ")"
                        : ""
                }`;
            },
            header: () => "Provider",
            enableSorting: false,
        },
    ),
    nonDetectsColumnHelper.accessor(
        (row) => {
            return {
                detectionLimit: row.detectionLimit,
            };
        },
        {
            id: "detectionLimit",
            header: "Detection Limit",
            cell: (info) => {
                const { detectionLimit } = info.getValue();
                return (
                    <RateWithUncertaintyCell
                        rate={detectionLimit / 1000}
                        unit="kg/h"
                    />
                );
            },
            sortingFn: (rowA, rowB) => {
                return (
                    rowA.original.detectionLimit - rowB.original.detectionLimit
                );
            },
        },
    ),
    nonDetectsColumnHelper.accessor(
        (row) => {
            return {
                id: row.id,
                sceneId: row.scene,
            };
        },
        {
            id: "extraData",
            header: "Extra data (admin-only)",
            cell: (info) => {
                const value = info.getValue();
                return (
                    <div>
                        NonDetectId: {value.id}
                        <br />
                        SceneId: {value.sceneId}
                    </div>
                );
            },
            enableSorting: false,
        },
    ),
];

interface NonDetectsTableProps {
    siteId: string;
    dateFrom: Date;
    dateTo: Date;
}

export const NonDetectsTable = (props: NonDetectsTableProps) => {
    const apiClient = useNondetectsApiClient();
    const isStaff = useAppSelector((s) => s.auth.isStaff);
    const columns = useMemo(() => {
        if (isStaff) {
            return nonDetectsColumns;
        }
        return nonDetectsColumns.filter((c) => c.id !== "extraData");
    }, [isStaff]);

    const fetchFunction = async (props: any) => {
        return await apiClient.nondetectsSitenondetectList(props);
    };

    const { table, dataQuery, dataName } = useDataTableV3<SiteNonDetect>({
        dataName: `site-${props.siteId}-emissions`,
        columns,
        extraFilters: {
            site: props.siteId,
            timestampAfter: props.dateFrom,
            timestampBefore: props.dateTo,
        },
        fetchFunction: fetchFunction,
        sortable: true,
        filterable: false,
    });

    return (
        <div className="flex-1 flex min-h-0 overflow-hidden">
            <CompactTableUI<SiteNonDetect>
                table={table}
                dataName={dataName}
                columns={columns}
                loadingData={dataQuery.isFetching}
                itemCount={dataQuery.data?.count || 0}
                showPagination={true}
            />
        </div>
    );
};
